import type { ReactNode } from "react";

import { COSIGNATURE_ACKNOWLEDGEMENT } from "@/features/cosignature/constants";
import type { CosignatureDocumentType, UUID } from "@/core/types";
import { testLocator } from "@/lib/helpers/test-locator";
import { TextArea2 } from "@/ui2/atoms/TextArea";
import { useAddendumCosignMeta } from "@/features/cosignature/api/queries/useAddendumCosignMeta";
import { useNoteCosignMeta } from "@/features/cosignature/api/queries/useNoteCosignMeta";

import { nameWithCredentials } from "./helpers/name-with-credentials";
import { NoteCosignature } from "./components/NoteCosignature";
import { NoteSignature } from "./components/NoteSignature";
import { ProviderSignature } from "./components/ProviderSignature";

type SignatureListProps = {
  className?: string;
  cosignatureBadge?: ReactNode;
  cosignedBy?: UUID;
  credentials?: string | null;
  documentId?: string;
  documentType?: CosignatureDocumentType;
  isCosignRequired?: boolean;
  isNoteComplete?: boolean;
  providerPractice?: string | null;
  signature?: string | null;
  signatureDate?: string | null;
  testId?: string;
};

export function SignatureList({
  className,
  cosignatureBadge,
  cosignedBy,
  credentials,
  documentId,
  documentType,
  isCosignRequired,
  isNoteComplete,
  providerPractice,
  signature,
  signatureDate,
  testId = "note-footer",
}: SignatureListProps) {
  const isAddendum = documentType === "note_addendum";
  const canFetchMeta = isCosignRequired && isNoteComplete && !!documentId;

  // documentType decides which of the two endpoints applies; both hooks are always
  // called (rules of hooks — SignatureList is mounted per-item in list contexts like
  // PrintNoteView) but mutually exclusive via `enabled`.
  const { data: noteMeta } = useNoteCosignMeta({
    documentId: documentId ?? "",
    enabled: !isAddendum && canFetchMeta,
  });
  const { data: addendumMeta } = useAddendumCosignMeta({
    documentId: documentId ?? "",
    enabled: isAddendum && canFetchMeta,
  });
  const cosignatureMeta = isAddendum ? addendumMeta : noteMeta;

  return (
    <>
      <footer className={className} {...testLocator(testId)}>
        <ul className="tw:flex tw:flex-wrap tw:items-start tw:justify-between tw:gap-4">
          {signature && (
            <NoteSignature label="Signature" {...testLocator("providerSignature")}>
              <ProviderSignature>
                {nameWithCredentials({ credentials, name: signature })}
              </ProviderSignature>
            </NoteSignature>
          )}

          {isCosignRequired && (
            <div className="tw:flex tw:max-w-1/3 tw:flex-col tw:gap-3">
              <NoteCosignature cosignatureBadge={cosignatureBadge} cosignedBy={cosignedBy} />

              {isNoteComplete && (
                <span className="tw:text-xs tw:text-text-secondary">
                  {COSIGNATURE_ACKNOWLEDGEMENT}
                </span>
              )}

              {cosignatureMeta?.acknowledgment_details && (
                <div className="tw:flex tw:border-t tw:border-stroke-quaternary tw:pt-0.5">
                  <TextArea2
                    aria-label="Additional Comments"
                    className="tw:bg-transparent tw:px-0"
                    id={`cosignature-acknowledgment-details-${documentId}`}
                    mode="display"
                    value={cosignatureMeta.acknowledgment_details}
                  />
                </div>
              )}
            </div>
          )}

          {signatureDate && <NoteSignature label="Signature Date">{signatureDate}</NoteSignature>}

          {providerPractice && (
            <NoteSignature label="Provider Practice">{providerPractice}</NoteSignature>
          )}
        </ul>
      </footer>
    </>
  );
}
